import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Check, ShoppingCart, CalendarCheck, FileText, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProductCard, Provisional, TestimonialCard, CtaBand, useList } from "@/components/Blocks";
import { PageHero } from "./About";
import { useSeo } from "@/lib/analytics";
import { useAuth, isStaff } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { money, MODE_LABEL, PURCHASABLE, AVAIL } from "@/lib/api";
import { useSite } from "@/lib/i18n";

export function SolutionsPage() {
  const { user } = useAuth();
  const products = useList(`/public/products${isStaff(user) ? "?preview=1" : ""}`, [user]);
  const [cat, setCat] = useState("all");
  useSeo({ title: "Soluciones y productos de software", description: "Catálogo de plataformas SaaS y sistemas empresariales de Cloud Center Costa Rica." });
  const cats = useMemo(() => [...new Set((products || []).map((p) => p.category).filter(Boolean))], [products]);
  const shown = (products || []).filter((p) => cat === "all" || p.category === cat);
  return (
    <>
      <PageHero eyebrow="Soluciones" title="Software propio para operar, facturar y crecer" sub="Plataformas SaaS y sistemas empresariales con modalidades de suscripción, licenciamiento, cotización y demostración." />
      <section className="wrap py-16 md:py-24">
        {isStaff(user) && <p className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="admin-preview-banner">Vista de administrador: se muestran también productos en borrador (no visibles al público).</p>}
        {cats.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2" role="tablist">
            {["all", ...cats].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`category-filter-${c}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "border-transparent bg-navy text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                {c === "all" ? "Todas" : c}
              </button>
            ))}
          </div>
        )}
        {products && !products.length ? (
          <Provisional testid="catalog-empty" title="Catálogo comercial en preparación" text="Nuestros productos se publicarán una vez aprobados su disponibilidad comercial, precios y materiales. Solicite una demostración para conocerlos." />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">{shown.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        )}
      </section>
      <CtaBand />
    </>
  );
}

function PlanCard({ p, plan, currency }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const { site } = useSite();
  const price = plan.mode === "trial" ? 0 : plan.prices?.[currency];
  const buyable = PURCHASABLE.includes(plan.mode) && p.status === "published" && p.availability === "available" && price !== undefined && price !== null && price !== "";
  return (
    <div className="card-cc flex flex-col" data-testid={`plan-card-${plan.id}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{MODE_LABEL[plan.mode]}</p>
      <h3 className="mt-2 font-display text-xl font-semibold">{plan.name}</h3>
      <p className="mt-4 font-display text-3xl font-bold" data-testid={`plan-price-${plan.id}`}>
        {PURCHASABLE.includes(plan.mode) ? (price !== undefined && price !== null && price !== "" ? money(price, currency) : <span className="text-base font-medium text-amber-600">Precio pendiente de aprobación</span>) : <span className="text-base font-medium text-slate-500">A la medida</span>}
        {["monthly", "annual"].includes(plan.mode) && price ? <span className="text-sm font-medium text-slate-500"> /{plan.mode === "monthly" ? "mes" : "año"}</span> : null}
      </p>
      {plan.mode === "trial" && <p className="text-sm text-slate-500">{plan.trial_days || 14} días de prueba</p>}
      <ul className="mt-5 flex-1 space-y-2">{(plan.features || []).map((f) => <li key={f} className="flex gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{f}</li>)}</ul>
      {PURCHASABLE.includes(plan.mode) && site.cart_enabled ? (
        <button disabled={!buyable} onClick={() => { add(p, plan); toast.success("Agregado al carrito"); }} data-testid={`add-to-cart-${plan.id}`}
          className="btn-cc mt-6 disabled:cursor-not-allowed disabled:opacity-50"><ShoppingCart className="h-4 w-4" /> Agregar al carrito</button>
      ) : (
        <button onClick={() => navigate(plan.mode === "demo" ? `/demo?producto=${encodeURIComponent(p.name)}` : `/contacto?interes=${encodeURIComponent(p.name + " · cotización")}`)}
          className="btn-ghost-cc mt-6 border-slate-300 text-slate-800 hover:bg-slate-50" data-testid={`plan-cta-${plan.id}`}>
          {plan.mode === "demo" ? <><CalendarCheck className="h-4 w-4" /> Solicitar demostración</> : <><FileText className="h-4 w-4" /> Solicitar cotización</>}
        </button>
      )}
    </div>
  );
}

const ListBlock = ({ title, items }) =>
  items?.length ? (
    <div>
      <p className="eyebrow">{title}</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">{items.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{x}</li>)}</ul>
    </div>
  ) : null;

export function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const p = useList(`/public/products/${slug}${isStaff(user) ? "?preview=1" : ""}`, [user]);
  const faqs = useList(`/public/faqs?product=${slug}`) || [];
  const testis = useList(`/public/testimonials?product=${slug}`) || [];
  const [currency, setCurrency] = useState("USD");
  const ok = p && !Array.isArray(p);
  const firstPrice = ok && (p.plans || []).find((pl) => pl.prices?.USD);
  useSeo({
    title: ok ? p.name : undefined, description: ok ? p.short_desc : undefined, image: ok ? p.logo_url : undefined,
    jsonLd: ok ? { "@context": "https://schema.org", "@type": "SoftwareApplication", name: p.name, applicationCategory: "BusinessApplication", description: p.short_desc,
      ...(firstPrice ? { offers: { "@type": "Offer", price: firstPrice.prices.USD, priceCurrency: "USD" } } : {}) } : undefined,
  });
  if (p === null) return <div className="wrap py-32 text-slate-500">Cargando…</div>;
  if (!ok) return <div className="wrap py-32 text-center"><h1 className="text-3xl font-bold">Producto no disponible</h1><Link to="/soluciones" className="btn-cc mt-6">Ver catálogo</Link></div>;
  const a = AVAIL[p.availability] || AVAIL.demo_only;
  const allFaqs = [...(p.faqs || []).map((f, i) => ({ id: `p${i}`, question: f.q || f.question, answer: f.a || f.answer })), ...faqs];
  return (
    <>
      <PageHero eyebrow={p.category} title={p.name} sub={p.short_desc}>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className={`rounded-full border px-3 py-1 text-xs font-medium ${a.cls}`} data-testid="product-availability">{a.label}</span>
          {p.status !== "published" && <span className="rounded-full border border-slate-500 px-3 py-1 text-xs text-slate-300">Borrador · visible solo para administradores</span>}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#planes" className="btn-cc" data-testid="product-plans-btn">Ver planes</a>
          <Link to={`/demo?producto=${encodeURIComponent(p.name)}`} className="btn-ghost-cc border-white/20 text-white hover:bg-white/10" data-testid="product-demo-btn">Solicitar demostración</Link>
        </div>
      </PageHero>
      <section className="wrap grid gap-12 py-16 md:py-24 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-10">
          <p className="text-lg leading-relaxed text-slate-700 whitespace-pre-line" data-testid="product-description">{p.description}</p>
          <ListBlock title="Características" items={p.features} />
          <ListBlock title="Beneficios" items={p.benefits} />
          <ListBlock title="Industrias" items={p.industries} />
          <ListBlock title="Requisitos técnicos" items={p.requirements} />
        </div>
        <div className="space-y-4">
          {p.screenshots?.length ? p.screenshots.map((s) => <img key={s} src={s} alt={`Captura de ${p.name}`} loading="lazy" className="w-full rounded-2xl border border-slate-200 shadow-lg" />)
            : <Provisional testid="screenshots-placeholder" title="Capturas reales del producto" text="Se publicarán capturas reales de la interfaz una vez aprobadas." />}
          {p.video_url && <a href={p.video_url} target="_blank" rel="noopener noreferrer" className="btn-ghost-cc w-full border-slate-300" data-testid="product-video-link"><PlayCircle className="h-4 w-4" /> Ver video demostrativo</a>}
        </div>
      </section>
      <section id="planes" className="border-y border-slate-200 bg-[color:var(--cc-bg)] py-16 md:py-24">
        <div className="wrap">
          <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div><p className="eyebrow">Planes y modalidades</p><h2 className="h2 mt-3">Elija cómo contratar {p.name}</h2></div>
            <div className="inline-flex rounded-full border border-slate-300 bg-white p-1" role="group" aria-label="Moneda">
              {["USD", "CRC"].map((c) => (
                <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${currency === c ? "bg-navy text-white" : "text-slate-600"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{(p.plans || []).map((pl) => <PlanCard key={pl.id} p={p} plan={pl} currency={currency} />)}</div>
          {p.availability !== "available" && <p className="mt-6 text-sm text-slate-500">Este producto no está habilitado para compra en línea. Solicite una demostración o cotización.</p>}
        </div>
      </section>
      {testis.length > 0 && (
        <section className="wrap py-16"><p className="eyebrow">Testimonios</p><div className="mt-6 grid gap-6 md:grid-cols-3">{testis.map((t) => <TestimonialCard key={t.id} t={t} />)}</div></section>
      )}
      {allFaqs.length > 0 && (
        <section className="wrap max-w-3xl py-16">
          <h2 className="h2">Preguntas frecuentes</h2>
          <Accordion type="single" collapsible className="mt-6">
            {allFaqs.map((f) => <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="text-left">{f.question}</AccordionTrigger><AccordionContent className="text-slate-600">{f.answer}</AccordionContent></AccordionItem>)}
          </Accordion>
        </section>
      )}
      <CtaBand />
    </>
  );
}
